import React, { useState, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  PhonicsLesson,
  PhonicsClassStatus,
  PhonicsClassLessonStatus,
  PhonicsLessonProgress,
  PhonicsLessonGroup,
} from '@/api/types';
import { useData } from '@/context/Data';
import { GreenBox } from '@/components';
import { ManagePanel } from './ManagePanel';

interface ManageLessonLayoutProps {
  lesson: PhonicsLesson;
  phonicsClass: PhonicsClassStatus;
  onGetYearGroup: (yearGroupId: string) => PhonicsLessonGroup | undefined;
}

export const ManageLessonLayout = ({
  children,
  lesson,
  phonicsClass,
  onGetYearGroup,
}: React.PropsWithChildren<ManageLessonLayoutProps>) => {
  const { currentClass } = useData();

  const [classLessonStatus, setClassLessonStatus] = useState<
    PhonicsClassLessonStatus | undefined
  >();

  const [readingTimeSpendMins, setReadingTimeSpendMins] = useState<
    number | undefined
  >();

  const [spellingTimeSpendMins, setSpellingTimeSpendMins] = useState<
    string | undefined
  >();

  const [lessonFinnishedTime, setLessonFinnishedTime] = useState<
    DateTime | undefined
  >();

  const yearGroup = useMemo(() => {
    if (!onGetYearGroup || !lesson.group) {
      return;
    }

    return onGetYearGroup(lesson.group.id);
  }, [lesson, onGetYearGroup]);

  useEffect(() => {
    if (!lesson) {
      return;
    }

    if (!phonicsClass) {
      return;
    }

    const thisLessonStatus = phonicsClass.classLessonStatuses.find(
      (lessonStatus) => lessonStatus.lessonId === lesson.id,
    );

    setClassLessonStatus(thisLessonStatus);

    if (thisLessonStatus?.progress === PhonicsLessonProgress.COMPLETED) {
      const completedAt = DateTime.fromISO(thisLessonStatus?.completedAt);
      const startedAt = DateTime.fromISO(thisLessonStatus?.startedAt);

      setReadingTimeSpendMins(completedAt.diff(startedAt).minutes);
    }

    setSpellingTimeSpendMins(undefined);
    setLessonFinnishedTime(undefined);
  }, [lesson, phonicsClass]);

  return (
    <>
      <ManagePanel
        yOrientation
        bgColorScheme="white"
        title={`${yearGroup?.name}, ${lesson.group?.name}, Lesson ${lesson.number}`}
      >
        <div className="flex gap-3">
          <div className="flex gap-6">
            {lesson.sounds ? (
              <GreenBox
                className="flex-none"
                size="lg"
                smallText={lesson.isReview}
                bg="wt"
                highlighted
              >
                {lesson.isReview ? (
                  <div className="absolute inset-0 flex p-3">Review Lesson</div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center font-sassoonInfant">
                    {lesson.sounds?.[0]?.name}
                  </div>
                )}
              </GreenBox>
            ) : null}

            {/* <DefinitionList
              className="self-start"
              items={{
                ...(lesson.isReview
                  ? {
                      'Is review': <LuCheck className="w-4 h-4" />,
                    }
                  : {}),
                ...(lesson.isExtra
                  ? {
                      'Is optional': <LuCheck className="w-4 h-4" />,
                    }
                  : {}),
                ...(lesson.sounds?.length
                  ? {
                      Sounds: lesson.sounds.map((sound) => sound.name).join(', '),
                    }
                  : {}),
                ...(lesson.patterns?.length
                  ? {
                      Patterns: lesson.patterns
                        .map((pattern) => pattern.name)
                        .join(', '),
                    }
                  : {}),
                ...(lesson.letterSets?.length
                  ? {
                      'Letter sets': lesson.letterSets.join(', '),
                    }
                  : {}),
                ...(lesson.step
                  ? {
                      'Sound discovery step': lesson.step,
                    }
                  : {}),
                ...(lesson.phase
                  ? {
                      'Letters & sounds phase': lesson.phase,
                    }
                  : {}),
                ...(lesson.trickyWords?.length
                  ? {
                      'Tricky words': lesson.trickyWords.join(', '),
                    }
                  : {}),
              }}
            /> */}

            <div className="flex flex-col gap-3">
              <div className="prose">
                <p className="text-sm font-bold text-green-dark">
                  We advise an average of 15 minutes spent on reading and 15
                  minutes spent on spelling for this lesson*
                </p>

                <p className="text-green-dark">
                  {(!classLessonStatus ||
                    classLessonStatus.progress ===
                      PhonicsLessonProgress.NOT_STARTED) && (
                    <>{currentClass?.label} has not started this lesson yet.</>
                  )}

                  {classLessonStatus?.progress ===
                    PhonicsLessonProgress.IN_PROGRESS && (
                    <>
                      {currentClass?.label} is currently working on this lesson.
                    </>
                  )}

                  {classLessonStatus?.progress ===
                    PhonicsLessonProgress.COMPLETED && (
                    <>{currentClass?.label} has completed this lesson.</>
                  )}
                </p>
              </div>

              {classLessonStatus &&
                [
                  PhonicsLessonProgress.COMPLETED,
                  PhonicsLessonProgress.IN_PROGRESS,
                ].includes(classLessonStatus.progress) && (
                  <ul className="flex flex-col gap-3 text-green-dark list-disc list-inside">
                    {readingTimeSpendMins !== undefined && (
                      <li>
                        Time spent reading: {readingTimeSpendMins} minutes
                        (group activities)
                      </li>
                    )}
                    {spellingTimeSpendMins && (
                      <li>Time spent spelling: {spellingTimeSpendMins} ()</li>
                    )}
                    {lessonFinnishedTime && (
                      <li>Lesson finnished: {lessonFinnishedTime.toISO()}</li>
                    )}
                  </ul>
                )}
            </div>

            {lesson.soundsNotes ? (
              <div className="p-3 flex-1 bg-green-light text-green-dark">
                {lesson.soundsNotes}
              </div>
            ) : null}
          </div>

          {lesson.learningObjective ? (
            <div className="p-3 flex-1 bg-green-light text-green-dark">
              <h2 className="mb-3 font-bold">Learning Objective</h2>

              {lesson.learningObjective}
            </div>
          ) : null}

          {lesson.successCriteria ? (
            <div className="p-3 flex-1 bg-green-light text-green-dark">
              <h2 className="mb-3 font-bold">Success criteria</h2>
              {lesson.successCriteria}
            </div>
          ) : null}
        </div>
      </ManagePanel>

      {children}
    </>
  );
};
